import { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import usePageTitle from '../hooks/usePageTitle';
import useCanonicalTag from '../hooks/useCanonicalTag';
import useMetaTags from '../hooks/useMetaTags';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getMarketplaceListing, markMarketplaceSold, startMarketplaceDeleteOAuth } from '../services/api';
import '../styles/marketplace.css';

function fmtPrice(listing, t) {
    if (listing.negotiable) return t('marketplace.negotiableLabel');
    if (listing.price) return `R$ ${parseFloat(listing.price).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
    return '—';
}

function fmtDate(dt) {
    if (!dt) return '';
    return new Date(dt).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
}

export default function MarketplaceDetail() {
    const { t } = useTranslation();
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const [listing, setListing] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeImg, setActiveImg] = useState(0);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [markingSold, setMarkingSold] = useState(false);
    const [deletingListing, setDeletingListing] = useState(false);

    usePageTitle(listing?.title);
    useCanonicalTag();
    useMetaTags({
        title: listing ? `${listing.title} | Celeritas Marketplace` : 'Celeritas Marketplace',
        description: listing?.description || 'Browse iRacing equipment and services on Celeritas Marketplace',
        image: listing?.images?.[0],
        url: `https://celeritas.live/marketplace/${id}`
    });

    const deleteError = searchParams.get('delete_error');

    const urlCustId = searchParams.get('cust_id');
    const urlToken = searchParams.get('marketplace_token');

    useEffect(() => {
        getMarketplaceListing(id)
            .then(data => { setListing(data); setLoading(false); })
            .catch(() => setLoading(false));
    }, [id]);

    useEffect(() => {
        if (!lightboxOpen) return;
        const handler = (e) => {
            if (e.key === 'Escape') setLightboxOpen(false);
            if (e.key === 'ArrowLeft') setActiveImg(i => (i - 1 + listing.images.length) % listing.images.length);
            if (e.key === 'ArrowRight') setActiveImg(i => (i + 1) % listing.images.length);
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [lightboxOpen, listing]);

    async function handleDelete() {
        if (!window.confirm(t('marketplace.deleteConfirm'))) return;
        setDeletingListing(true);
        try {
            const data = await startMarketplaceDeleteOAuth(id);
            window.location.href = data.oauth_url;
        } catch {
            setDeletingListing(false);
        }
    }

    async function handleMarkSold() {
        if (!window.confirm('Marcar este anúncio como vendido?')) return;
        setMarkingSold(true);
        try {
            await markMarketplaceSold(id, urlCustId, urlToken);
            setListing(prev => ({ ...prev, status: 'sold' }));
        } catch { } finally { setMarkingSold(false); }
    }

    if (loading) return <div className="App"><Header /><main className="main-content"><p style={{ padding: 40, color: '#9ba3ab' }}>{t('common.loading')}</p></main><Footer /></div>;
    if (!listing) return <div className="App"><Header /><main className="main-content"><p style={{ padding: 40, color: '#9ba3ab' }}>Anúncio não encontrado.</p></main><Footer /></div>;

    const isOwner = urlCustId && parseInt(urlCustId) === listing.cust_id;
    const images = listing.images || [];
    const location = [listing.city, listing.state, listing.country].filter(Boolean).join(', ');

    return (
        <div className="App">
            <Header />

            {lightboxOpen && images.length > 0 && (
                <div className="mp-lightbox" onClick={() => setLightboxOpen(false)}>
                    <button className="mp-lightbox-close" onClick={() => setLightboxOpen(false)}>✕</button>
                    <img src={images[activeImg].image_url} alt="" onClick={e => e.stopPropagation()} />
                    {images.length > 1 && (
                        <>
                            <button className="mp-carousel-btn mp-carousel-prev mp-lb-btn"
                                onClick={e => { e.stopPropagation(); setActiveImg(i => (i - 1 + images.length) % images.length); }}>‹</button>
                            <button className="mp-carousel-btn mp-carousel-next mp-lb-btn"
                                onClick={e => { e.stopPropagation(); setActiveImg(i => (i + 1) % images.length); }}>›</button>
                            <span className="mp-lightbox-count">{activeImg + 1} / {images.length}</span>
                        </>
                    )}
                </div>
            )}

            <main className="main-content">
                <div className="marketplace-detail">
                    <Link to="/marketplace" className="marketplace-detail-back">{t('marketplace.backToList')}</Link>

                    {images.length > 0 && (
                        <div className="marketplace-gallery">
                            <img
                                src={images[activeImg].image_url}
                                alt={listing.title}
                                className="marketplace-gallery-main"
                                onClick={() => setLightboxOpen(true)}
                                style={{ cursor: 'zoom-in' }}
                            />
                            {images.length > 1 && images.map((img, i) => (
                                <img
                                    key={img.id}
                                    src={img.image_url}
                                    alt=""
                                    onClick={() => setActiveImg(i)}
                                    style={{ opacity: i === activeImg ? 1 : 0.5, cursor: 'pointer' }}
                                />
                            ))}
                        </div>
                    )}

                    {deleteError && (
                        <div style={{ background: '#3d2020', color: '#e07070', borderRadius: 6, padding: '10px 16px', marginBottom: 16, fontSize: '0.85rem' }}>
                            {deleteError === 'not_author' && t('marketplace.deleteErrorNotAuthor')}
                            {deleteError === 'auth_failed' && t('marketplace.deleteErrorAuthFailed')}
                            {deleteError === 'not_found' && t('marketplace.deleteErrorNotFound')}
                        </div>
                    )}

                    <div className="marketplace-delete-section">
                        <p className="marketplace-delete-hint">{t('marketplace.deleteHint')}</p>
                        <button className="marketplace-delete-btn" onClick={handleDelete} disabled={deletingListing}>
                            {deletingListing ? t('common.loading') : t('marketplace.deleteBtn')}
                        </button>
                    </div>

                    <div className="marketplace-detail-grid">
                        <div className="marketplace-detail-info">
                            {listing.status === 'sold' && (
                                <span className="marketplace-badge sold" style={{ marginBottom: 8, display: 'inline-block' }}>
                                    {t('marketplace.soldBadge')}
                                </span>
                            )}
                            <h2>{listing.title}</h2>
                            <div className="marketplace-detail-price">{fmtPrice(listing, t)}</div>
                            <p style={{ color: '#9ba3ab', fontSize: '0.8rem', marginBottom: 16 }}>
                                {t('marketplace.postedAt')} {fmtDate(listing.created_at)}
                                {location && ` · 📍 ${location}`}
                            </p>
                            <div className="marketplace-detail-desc">{listing.description}</div>
                        </div>

                        <div className="marketplace-detail-sidebar">
                            <h3>{t('marketplace.verifiedSeller')}</h3>
                            <div className="marketplace-seller-name">{listing.display_name}</div>
                            <div className="marketplace-seller-verified">✓ iRacing ID: {listing.cust_id}</div>

                            <div style={{ marginTop: 20 }}>
                                <h3>{t('marketplace.contact')}</h3>
                                <div className="marketplace-contact-info">
                                    {listing.contact_email && (
                                        <div>✉ <a href={`mailto:${listing.contact_email}`} style={{ color: '#e8c84a' }}>{listing.contact_email}</a></div>
                                    )}
                                    {listing.contact_phone && (
                                        <div>📱 {listing.contact_phone}</div>
                                    )}
                                    {listing.contact_discord && (
                                        <div>💬 {t('marketplace.discordIdLabel')}: {listing.contact_discord}</div>
                                    )}
                                </div>
                            </div>

                            {isOwner && listing.status === 'active' && (
                                <button className="marketplace-mark-sold" onClick={handleMarkSold} disabled={markingSold}>
                                    {markingSold ? t('common.loading') : t('marketplace.markSold')}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
}
